import { Link, useParams } from "react-router-dom";
import * as Lucide from "lucide-react";
import { Seo } from "@/components/Seo";
import { CtaBand, Breadcrumbs, Faq, LinkGrid } from "@/components/Shared";
import { SERVICES, CITIES, BRAND } from "@/data/site";
import { url, metaServiceCity, metaServiceProvince, serviceBySlug, provBySlug, cityBySlug, citiesInProv, serviceCityContent, serviceProvinceContent, breadcrumbLd, faqLd, serviceLd, POPULAR_SEARCHES } from "@/data/content";
import NotFound from "@/pages/NotFound";

const Icon = ({ name, className }) => {
  const C = Lucide[name] || Lucide.Shield;
  return <C className={className} />;
};

const STEPS = [
  { t: "Call or Email Dispatch", d: `Reach our 24/7 desk at ${BRAND.phone} or ${BRAND.email} and describe your site and risk concerns.` },
  { t: "Free Risk Assessment & Written Quote", d: "We assess your property, recommend coverage and send an itemized written quote — the same day." },
  { t: "Licensed Guards Deployed", d: "Provincially licensed, insured officers start within 24-48 hours, or within hours for emergencies." },
  { t: "Supervised & Reported", d: "Field supervisors audit every contract; you receive GPS-verified logs and incident reports automatically." },
];

const ProcessSteps = ({ area }) => (
  <section className="my-14" data-testid="process-steps">
    <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight mb-8">How Deployment Works in {area}</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {STEPS.map((st, i) => (
        <div key={st.t} className="relative rounded-xl border border-slate-800 bg-[#111827] p-6">
          <span className="font-mono text-3xl font-bold text-amber-500/30">0{i + 1}</span>
          <h3 className="font-semibold text-slate-100 mt-2 mb-2 text-sm sm:text-base">{st.t}</h3>
          <p className="text-sm text-slate-400 leading-relaxed">{st.d}</p>
        </div>
      ))}
    </div>
  </section>
);

const FeatureGrid = ({ s }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 my-10">
    {s.features.map((f) => (
      <div key={f.t} className="rounded-xl border border-slate-800 bg-[#111827] p-6">
        <Lucide.CheckCircle2 className="h-5 w-5 text-amber-400 mb-3" />
        <h3 className="font-semibold text-slate-100 mb-1.5 text-sm sm:text-base">{f.t}</h3>
        <p className="text-sm text-slate-400 leading-relaxed">{f.d}</p>
      </div>
    ))}
  </div>
);

const AreaHero = ({ h1, eyebrow, children, testid }) => (
  <section className="relative overflow-hidden border-b border-white/5">
    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(245,158,11,0.12) 0%, transparent 65%)" }} />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14 relative">
      {children[0]}
      <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] text-amber-500 mb-4">{eyebrow}</span>
      <h1 data-testid={testid} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-display tracking-tight text-slate-50 max-w-4xl">{h1}</h1>
      <div className="mt-6 flex flex-wrap gap-3">
        <a href={BRAND.phoneHref} data-testid="area-call-cta" className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-slate-950 hover:bg-amber-400 transition-colors">
          <Lucide.Phone className="h-4 w-4" /> {BRAND.phone}
        </a>
        <a href={BRAND.emailHref} data-testid="area-email-cta" className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-6 py-3 text-sm font-semibold text-slate-100 hover:border-amber-500/60 hover:text-amber-400 transition-colors">
          <Lucide.Mail className="h-4 w-4" /> {BRAND.email}
        </a>
      </div>
      {children[1]}
    </div>
  </section>
);

function ServiceCityPage({ s, c }) {
  const p = provBySlug(c.prov);
  const meta = metaServiceCity(s, c);
  const cc = serviceCityContent(s, c);
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services/" },
    { name: s.name, path: url.service(s) },
    { name: c.name, path: url.serviceCity(s, c) },
  ];
  const siblings = citiesInProv(c.prov).filter((x) => x.slug !== c.slug);
  const otherServices = SERVICES.filter((x) => x.slug !== s.slug);
  return (
    <div data-testid={`service-city-page-${s.slug}-${c.slug}`}>
      <Seo title={meta.title} description={meta.description} path={url.serviceCity(s, c)} jsonLd={[serviceLd(s, `${c.name}, ${p.abbr}`), faqLd(cc.faqs), breadcrumbLd(crumbs)]} />
      <AreaHero h1={cc.h1} eyebrow={`Licensed • Insured • 24/7 Dispatch in ${c.name}`} testid="service-city-h1">
        <Breadcrumbs items={crumbs} />
        <div className="mt-7 flex flex-wrap gap-2">
          {["Licensed in " + p.name, "24/7 Dispatch", "Fully Insured", "GPS-Verified Reports"].map((chip) => (
            <span key={chip} className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-400">
              <Lucide.BadgeCheck className="h-3 w-3" /> {chip}
            </span>
          ))}
        </div>
      </AreaHero>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="space-y-5 text-slate-300 text-sm sm:text-base leading-relaxed">
              {cc.intro.map((para, i) => <p key={i} className={i === 0 ? "text-base sm:text-lg text-slate-200" : ""}>{para}</p>)}
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight mt-12 mb-2">What Our {s.name} Include in {c.name}</h2>
            <p className="text-sm text-slate-500 mb-2 font-mono uppercase tracking-widest">{s.kw.join(" • ")}</p>
            <FeatureGrid s={s} />

            <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight mt-4">{cc.localH}</h2>
            <div className="space-y-5 mt-5 text-slate-300 text-sm sm:text-base leading-relaxed">
              {cc.localPs.map((para, i) => <p key={i}>{para}</p>)}
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight mt-12">{cc.whyH}</h2>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {cc.why.map((w) => (
                <li key={w} className="flex items-start gap-3 rounded-xl border border-slate-800 bg-[#111827] px-4 py-3.5 text-sm text-slate-300">
                  <Lucide.CheckCircle2 className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" /> {w}
                </li>
              ))}
            </ul>
            <p className="mt-8 text-slate-300 text-sm sm:text-base leading-relaxed">{cc.closing}</p>

            <ProcessSteps area={c.name} />
            <Faq items={cc.faqs} heading={`${s.name} in ${c.name} — FAQs`} />
          </div>

          <aside className="space-y-6">
            <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-b from-[#111827] to-slate-900 p-6 sticky top-28">
              <Icon name={s.icon} className="h-8 w-8 text-amber-400 mb-4" />
              <h3 className="font-display font-bold text-slate-50 mb-2">{s.name} — {c.name}</h3>
              <p className="text-xs text-slate-400 leading-relaxed mb-5">Same-day written quotes. Emergency deployment available tonight across {c.metro}.</p>
              <a href={BRAND.phoneHref} data-testid="sidebar-call-cta" className="flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-3 text-sm font-bold text-slate-950 hover:bg-amber-400 transition-colors mb-3">
                <Lucide.Phone className="h-4 w-4" /> {BRAND.phone}
              </a>
              <a href={BRAND.emailHref} data-testid="sidebar-email-cta" className="flex items-center justify-center gap-2 rounded-full border border-slate-600 px-5 py-3 text-sm font-semibold text-slate-100 hover:border-amber-500/60 hover:text-amber-400 transition-colors">
                <Lucide.Mail className="h-4 w-4" /> Email Dispatch
              </a>
              <div className="mt-6 pt-5 border-t border-white/5 space-y-2.5 text-xs text-slate-400">
                <p className="flex items-center gap-2"><Lucide.Clock className="h-3.5 w-3.5 text-amber-500" /> Open 24 hours, 365 days</p>
                <p className="flex items-center gap-2"><Lucide.MapPin className="h-3.5 w-3.5 text-amber-500" /> Serving {c.name}, {p.name}</p>
                <p className="flex items-center gap-2"><Lucide.FileCheck className="h-3.5 w-3.5 text-amber-500" /> Written, itemized quotes</p>
              </div>
            </div>
          </aside>
        </div>

        <LinkGrid
          testid="related-services"
          title={`Other Security Services in ${c.name}`}
          links={otherServices.map((x) => ({ path: url.serviceCity(x, c), label: `${x.name} ${c.name}` }))}
        />
        {siblings.length > 0 && (
          <LinkGrid
            testid="sibling-cities"
            title={`${s.name} Elsewhere in ${p.name}`}
            links={siblings.map((x) => ({ path: url.serviceCity(s, x), label: `${s.name} ${x.name}` }))}
          />
        )}

        <section className="my-14 rounded-2xl border border-slate-800 bg-[#111827] p-8" data-testid="popular-searches">
          <h2 className="text-sm font-mono uppercase tracking-widest text-amber-500 mb-4">Popular Security Searches in {c.name}</h2>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {POPULAR_SEARCHES.map((k) => (
              <Link key={k} to={url.city(c)} className="text-sm text-slate-400 hover:text-amber-400 transition-colors">{k} {c.name}</Link>
            ))}
          </div>
        </section>

        <CtaBand heading={`Hire ${s.name} in ${c.name} Today`} sub={`Licensed ${s.kw[0]} across ${c.metro}. Call ${BRAND.phone} or email ${BRAND.email} for a same-day written quote.`} />
      </div>
    </div>
  );
}

function ServiceProvincePage({ s, p }) {
  const meta = metaServiceProvince(s, p);
  const pc = serviceProvinceContent(s, p);
  const cities = citiesInProv(p.slug);
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services/" },
    { name: s.name, path: url.service(s) },
    { name: p.name, path: url.serviceProv(s, p) },
  ];
  return (
    <div data-testid={`service-province-page-${s.slug}-${p.slug}`}>
      <Seo title={meta.title} description={meta.description} path={url.serviceProv(s, p)} jsonLd={[serviceLd(s, p.name), faqLd(pc.faqs), breadcrumbLd(crumbs)]} />
      <AreaHero h1={pc.h1} eyebrow={`Licensed ${p.name} Guards — 24/7 Provincial Coverage`} testid="service-province-h1">
        <Breadcrumbs items={crumbs} />
        <span />
      </AreaHero>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="max-w-3xl space-y-5 text-slate-300 text-sm sm:text-base leading-relaxed">
          {pc.intro.map((para, i) => <p key={i} className={i === 0 ? "text-base sm:text-lg text-slate-200" : ""}>{para}</p>)}
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-50 tracking-tight mt-12">What Our {s.name} Include in {p.name}</h2>
        <FeatureGrid s={s} />
        <LinkGrid
          testid="province-cities"
          title={`${s.name} by City in ${p.name}`}
          links={cities.map((c) => ({ path: url.serviceCity(s, c), label: `${s.name} ${c.name}` }))}
        />
        <LinkGrid
          testid="province-other-services"
          title={`More Security Services in ${p.name}`}
          links={SERVICES.filter((x) => x.slug !== s.slug).map((x) => ({ path: url.serviceProv(x, p), label: `${x.name} ${p.name}` }))}
        />
        <ProcessSteps area={p.name} />
        <Faq items={pc.faqs} heading={`${s.name} in ${p.name} — FAQs`} />
        <CtaBand heading={`Hire ${s.name} Anywhere in ${p.name}`} sub={`One call covers every ${p.name} location: ${BRAND.phone}. Written quotes the same day.`} />
      </div>
    </div>
  );
}

export default function ServiceAreaPage() {
  const { serviceSlug, areaSlug } = useParams();
  const s = serviceBySlug(serviceSlug);
  if (!s) return <NotFound />;
  const p = provBySlug(areaSlug);
  if (p) return <ServiceProvincePage s={s} p={p} />;
  const c = cityBySlug(areaSlug);
  if (c) return <ServiceCityPage s={s} c={c} />;
  return <NotFound />;
}
